import {
    LockSuccessToast,
    DepositSuccessToast,
    MintSuccessToast,
    RedeemSuccessToast,
    WithdrawSuccessToast,
    StakeSuccessToast,
    WithdrawLockSuccessToast,
    CancelExitSuccessToast,
    ClaimSuccessToast,
} from "@/components";
import { useEventListener } from "@/hooks";
import {
    LOCK_EVENTS,
    DEPOSIT_EVENTS,
    MINT_EVENTS,
    REDEEM_EVENTS,
    type ToastEvents,
    WITHDRAW_EVENTS,
    type LockSuccessPayload,
    type MintSuccessPayload,
    type DepositSuccessPayload,
    type WithdrawSuccessPayload,
    type RedeemSuccessPayload,
    type StakeSuccessPayload,
    STAKE_EVENTS,
    REALLOCATE_POINTS_EVENTS,
    type ReallocatePointsSuccessPayload,
    type LockWithdrawPayload,
    CANCEL_EXIT_EVENTS,
    type CancelExitSuccessPayload,
    CLAIM_EVENTS,
    type ClaimSuccessPayload,
    MULTIVOTE_EVENTS,
    type MultiVoteSuccessPayloads,
    GENERIC_TRANSACTION_EVENTS,
    type GenericTransactionFailedPayload,
    TOAST_EVENTS,
} from "@/events";
import { useState, type ComponentType } from "react";
import { MultivoteSuccessToast } from "@/components/toasts/MultivoteSuccessToast";
import { TransactionFailedToast } from "@/components/toasts/TransactionFailedToast";
import { ReallocatePointsSuccessToast } from "@/components/toasts/PointsReallocateSuccessToast";

const TOAST_COMPONENTS: Record<ToastEvents, ComponentType> = {
    [TOAST_EVENTS.LOCK_SUCCESS]: LockSuccessToast,
    [TOAST_EVENTS.MINT_SUCCESS]: MintSuccessToast,
    [TOAST_EVENTS.DEPOSIT_SUCCESS]: DepositSuccessToast,
    [TOAST_EVENTS.WITHDRAW_SUCCESS]: WithdrawSuccessToast,
    [TOAST_EVENTS.REDEEM_SUCCESS]: RedeemSuccessToast,
    [TOAST_EVENTS.STAKE_SUCCESS]: StakeSuccessToast,
    [TOAST_EVENTS.REALLOCATE_POINTS_SUCCESS]: ReallocatePointsSuccessToast,
    [TOAST_EVENTS.WITHDRAW_LOCK_SUCCESS]: WithdrawLockSuccessToast,
    [TOAST_EVENTS.CANCEL_EXIT_SUCCESS]: CancelExitSuccessToast,
    [TOAST_EVENTS.CLAIM_SUCCESS]: ClaimSuccessToast,
    [TOAST_EVENTS.MULTIVOTE_SUCCESS]: MultivoteSuccessToast,
    [TOAST_EVENTS.GENERIC_TRANSACTION_FAILED]: TransactionFailedToast,
};

export function ToastPortal() {
    const [activeToast, setActiveToast] = useState<ToastEvents | null>(null);
    const [toastProps, setToastProps] = useState<Record<string, unknown> | null>(null);

    const handleCloseToast = () => {
        setActiveToast(null);
        setToastProps(null);
    };

    const handleShowToast = (type: ToastEvents, props: Record<string, unknown>) => {
        props.onClose = handleCloseToast;
        setActiveToast(type);
        setToastProps(props);
    };

    useEventListener(LOCK_EVENTS.SUCCESS, (payload: LockSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.LOCK_SUCCESS, {
            lockAmount: payload.lockAmount,
            lockTokenId: payload.lockTokenId,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(MINT_EVENTS.SUCCESS, (payload: MintSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.MINT_SUCCESS, {
            mintAmount: payload.mintAmount,
            mintTokenId: payload.mintTokenId,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(DEPOSIT_EVENTS.SUCCESS, (payload: DepositSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.DEPOSIT_SUCCESS, {
            depositAmount: payload.depositAmount,
            depositTokenId: payload.depositTokenId,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(WITHDRAW_EVENTS.SUCCESS, (payload: WithdrawSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.WITHDRAW_SUCCESS, {
            withdrawAmount: payload.withdrawAmount,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(REDEEM_EVENTS.SUCCESS, (payload: RedeemSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.REDEEM_SUCCESS, {
            redeemAmount: payload.redeemAmount,
            redeemTokenId: payload.redeemTokenId,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(STAKE_EVENTS.SUCCESS, (payload: StakeSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.STAKE_SUCCESS, {
            stakeAmount: payload.stakeAmount,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(REALLOCATE_POINTS_EVENTS.SUCCESS, (payload: ReallocatePointsSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.REALLOCATE_POINTS_SUCCESS, {
            newAddress: payload.newAddress,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(LOCK_EVENTS.WITHDRAW, (payload: LockWithdrawPayload) => {
        handleShowToast(TOAST_EVENTS.WITHDRAW_LOCK_SUCCESS, {
            withdrawAmount: payload.withdrawAmount,
            lockDuration: payload.lockDuration,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(CANCEL_EXIT_EVENTS.SUCCESS, (payload: CancelExitSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.CANCEL_EXIT_SUCCESS, {
            transactionHash: payload.transactionHash,
            newUnwindingEpochs: payload.newUnwindingEpochs,
        });
    });

    useEventListener(CLAIM_EVENTS.SUCCESS, (payload: ClaimSuccessPayload) => {
        handleShowToast(TOAST_EVENTS.CLAIM_SUCCESS, {
            claimAmount: payload.claimAmount,
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(MULTIVOTE_EVENTS.SUCCESS, (payload: MultiVoteSuccessPayloads) => {
        handleShowToast(TOAST_EVENTS.CLAIM_SUCCESS, {
            transactionHash: payload.transactionHash,
        });
    });

    useEventListener(GENERIC_TRANSACTION_EVENTS.FAILED, (payload: GenericTransactionFailedPayload) => {
        handleShowToast(TOAST_EVENTS.GENERIC_TRANSACTION_FAILED, {
            error: payload.error,
            context: payload.context,
            txHash: payload.txHash,
        });
    });

    if (!activeToast || !toastProps) return null;

    const ToastComponent = TOAST_COMPONENTS[activeToast];

    return <ToastComponent {...toastProps} />;
}
